// HELPERS (docs/design/2026-10-30-helpers-visibility.md; Andrei 2026-10-30: a two-person customer
// sees two DEs and asks "what is happening"): the agents (AG) behind each DE, shown as its
// "Helpers". Count always, names on demand, never a row at the DE's level:
//   - one read, the cached agents list (`workforceAgentsQuery`, the Workforce sidebar's and the
//     roster's), grouped by whom each reports to (a DE, or a person for a person-led helper);
//   - a helper is "working" while it is active and reported activity in the last ten minutes,
//     else idle (two states; pausing is the DE's or the roster's);
//   - counts show only when greater than zero, so a DE without helpers looks as before;
//   - view-only plus "Ask the DE": the DM with the helper's DE, its composer prefilled
//     "About Brand Critic: ". A helper is never a DM party, a mention or an inbox source.
// Skills are chips on a helper's card (its `skills` keys; a skill pack's key links to the pack).
import { Button, cn, InfoTip, toast, WorkerBadge, workerName } from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDownIcon, MessageSquareIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { WorkforceAgentRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { appendToDraft, draftKey } from "../chat/chat-switch";
import type { CompanyAccess } from "../company";
import { problemText } from "../ops-parts";
import { workforceAgentsQuery } from "../queries";
import { useEveryPage } from "../use-every-page";
import { AgActions, activityLabel, useAgChoices } from "./ag-actions";
import { reachRefusalToast } from "./ask-access";
import { AskForHelperButton, HelperRequestsList } from "./helper-requests";
import { isBorrowed, StopUsingButton, UseHelperButton } from "./helper-use";
import { RecallKeysChip } from "./recall-keys-chip";
import { skillPacksQuery } from "./skill-packs-screen";

export type Helper = WorkforceAgentRow;

/** How recent a helper's reported activity is for it to count as working (design §4, rule 3). */
export const HELPER_WORKING_MS = 10 * 60_000;

/** The (i) beside the Helpers caption, once (design §1). */
export const HELPERS_INFO =
  "Helpers (AG) work on tasks their digital employee gives them. No seat, no KPIs, no budget of their own.";

/** Working: active, with activity reported in the last ten minutes. Otherwise idle. */
export function helperWorking(
  a: Pick<Helper, "status" | "last_activity_at">,
  now: number = Date.now(),
): boolean {
  if (a.status !== "active" || !a.last_activity_at) return false;
  const at = Date.parse(a.last_activity_at);
  return Number.isFinite(at) && now - at <= HELPER_WORKING_MS && at - now <= 60_000;
}

/**
 * The helpers of each lead (DE or person membership), by name. Retired ones are left out, and so
 * are not-deployed ones (founder 2026-10-30): those are the "Available helpers" a client can ask
 * for (`availableHelpers`), not a DE's own. HELPERS-2: a helper shows under its home (whom it
 * reports to) and under every other DE that uses it (`used_by`).
 */
export function helpersByLead(rows: readonly Helper[]): Map<string, Helper[]> {
  const out = new Map<string, Helper[]>();
  const add = (lead: string, a: Helper) => {
    const list = out.get(lead) ?? [];
    if (!list.includes(a)) list.push(a);
    out.set(lead, list);
  };
  for (const a of rows) {
    if (a.status === "retired" || a.status === "not_deployed") continue;
    add(a.reports_to_membership_id, a);
    for (const u of a.used_by ?? []) add(u.membership_id, a);
  }
  for (const list of out.values()) list.sort((x, y) => x.name.localeCompare(y.name));
  return out;
}

/** The not-deployed helpers: the catalogue a client may ask for, by name. */
export function availableHelpers(rows: readonly Helper[]): Helper[] {
  return rows
    .filter((a) => a.status === "not_deployed")
    .sort((x, y) => x.name.localeCompare(y.name));
}

/** A clock that ticks every minute, so "working" lapses ten minutes after the last activity. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** The company's helpers by lead, from the cached agents list (no extra call where it is read). */
export function useHelpers(api: Api, co: string, enabled = true) {
  const q = useQuery({ ...workforceAgentsQuery(api, co), enabled, retry: false });
  const now = useMinuteClock();
  const byLead = useMemo(() => helpersByLead(q.data ?? []), [q.data]);
  const available = useMemo(() => availableHelpers(q.data ?? []), [q.data]);
  return { byLead, available, all: q.data ?? [], ready: q.isSuccess, now };
}

/** How many helpers and how many of them are working. */
export function helperCounts(list: readonly Helper[] | undefined, now = Date.now()) {
  const total = list?.length ?? 0;
  const working = (list ?? []).filter((a) => helperWorking(a, now)).length;
  return { total, working };
}

/** "14 helpers · 3 working", "1 helper"; "" with none. */
export function helpersText(total: number, working = 0): string {
  if (total <= 0) return "";
  const n = `${total} ${total === 1 ? "helper" : "helpers"}`;
  return working > 0 ? `${n} · ${working} working` : n;
}

/** The distinct skills of a DE (its own role skills) and its helpers. */
export function skillTotal(own: readonly string[], helpers: readonly Helper[]): number {
  return new Set([...own, ...helpers.flatMap((a) => a.skills ?? [])]).size;
}

/** "14 helpers · 31 skills" (the DE page header); "" with no helpers. */
export function helpersSkillsText(total: number, skills: number): string {
  if (total <= 0) return "";
  const s = skills > 0 ? ` · ${skills} ${skills === 1 ? "skill" : "skills"}` : "";
  return `${helpersText(total)}${s}`;
}

/** The muted count on a lead's row: `14`, or `3/14` in accent while some work. Null at zero. */
export function HelperCount({
  total,
  working,
  className,
}: {
  total: number;
  working: number;
  className?: string;
}) {
  if (total <= 0) return null;
  return (
    <span
      data-slot="helper-count"
      data-working={working > 0 ? "true" : undefined}
      title={helpersText(total, working)}
      className={cn(
        "shrink-0 text-xs tabular-nums",
        working > 0 ? "font-medium text-primary-text" : "text-faint",
        className,
      )}
    >
      <span aria-hidden="true">{working > 0 ? `${working}/${total}` : total}</span>
      <span className="sr-only">{helpersText(total, working)}</span>
    </span>
  );
}

/** The (i) beside a Helpers caption. */
export function HelpersInfoTip() {
  return <InfoTip label="About helpers">{HELPERS_INFO}</InfoTip>;
}

/** A helper's two states as a small dot (green working, grey idle). */
export function HelperDot({ working }: { working: boolean }) {
  return (
    <span
      data-slot="helper-dot"
      data-working={working ? "true" : undefined}
      role="img"
      aria-label={working ? "Working" : "Idle"}
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        working ? "bg-success" : "bg-border-strong",
      )}
    />
  );
}

/**
 * Where a helper opens: its DE's page › Helpers, scrolled to it; a person-led helper (no DE) its
 * own agent page.
 */
export function helperHref(
  company: Pick<CompanyAccess, "slug">,
  a: Pick<Helper, "id" | "reports_to_kind" | "reports_to_membership_id">,
) {
  return a.reports_to_kind === "digital_employee"
    ? ({
        to: "/c/$slug/agents/de/$id",
        params: { slug: company.slug, id: a.reports_to_membership_id },
        search: (prev: Record<string, unknown>) => ({
          ...keepFocus(prev),
          tab: "helpers",
          helper: a.id,
        }),
      } as const)
    : ({
        to: "/c/$slug/agents/ag/$id",
        params: { slug: company.slug, id: a.id },
        search: keepFocus,
      } as const);
}

/** The composer text "Ask the DE" starts with. */
export const askPrefill = (helperName: string) => `About ${workerName(helperName)}: `;

/**
 * "Ask the DE": opens (or reopens) the viewer's DM with the helper's DE and puts "About <helper>: "
 * in its composer (the DM's own draft; a draft already there keeps its text, the line goes after
 * it). Never a message to the helper itself.
 */
export function useAskTheDe(api: Api, company: CompanyAccess, meId: string | null) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (v: { deId: string; helperName: string }) =>
      api.client.dms.open({ params: { co: company.id }, body: { membership_ids: [v.deId] } }),
    onSuccess: (channel, v) => {
      if (meId) {
        const key = draftKey(company.id, meId, `stream:${channel.id}`);
        const line = {
          type: "paragraph",
          content: [{ type: "text", text: askPrefill(v.helperName), styles: {} }],
        };
        // Appends and tells an already-open composer to reload its draft (Codex: a DM that was
        // open kept its old text and the line was lost on its next save).
        appendToDraft(key, [line]);
      }
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: company.slug, channelId: channel.id },
        search: keepFocus,
      } as never);
    },
    onError: (e) => {
      if (reachRefusalToast(api, company.id, e)) return;
      toast.add({
        title: "Could not open the DM",
        description: problemText(e, e.message),
        type: "error",
      });
    },
  });
}

/**
 * Skills as chips, every one shown; each opens its entry in Workforce › Skills (the library: who
 * else uses it, and its skill pack when it is one).
 */
export function SkillChips({
  skills,
  company,
}: {
  skills: readonly string[];
  company: Pick<CompanyAccess, "slug">;
}) {
  if (!skills.length) return null;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1" data-slot="skill-chips">
      {skills.map((k) => (
        <Link
          key={k}
          to="/c/$slug/agents/skill-library"
          params={{ slug: company.slug }}
          search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), skill: k })}
          data-skill={k}
          className="rounded-full border border-subtle px-1.5 text-2xs leading-4 text-muted-foreground hover:border-strong hover:text-foreground"
        >
          {k}
        </Link>
      ))}
    </span>
  );
}

/**
 * The company's published skill packs by key (the newest version of each), every page read
 * (Codex: a deprecated pack was linked, and packs past the first page were missed).
 */
export function useSkillPackIndex(api: Api, co: string, enabled: boolean) {
  const packs = useInfiniteQuery({ ...skillPacksQuery(api, co), enabled, retry: false });
  useEveryPage(packs);
  return useMemo(() => {
    const out = new Map<string, { id: string; version: string }>();
    for (const p of packs.data?.pages.flatMap((x) => x.data) ?? [])
      if (!out.has(p.key) && !p.archived_at && p.status === "published")
        out.set(p.key, { id: p.id, version: p.version });
    return out;
  }, [packs.data]);
}

/** "14" or "3/14" with a chevron: opens a lead's helpers under its row. Nothing at zero. */
export function HelpersToggle({
  lead,
  counts,
  open,
  onToggle,
  className,
}: {
  lead: string;
  counts: { total: number; working: number };
  open: boolean;
  onToggle: () => void;
  className?: string;
}) {
  if (!counts.total) return null;
  return (
    <button
      type="button"
      data-slot="helpers-toggle"
      aria-expanded={open}
      aria-label={`${open ? "Hide" : "Show"} ${lead}'s helpers (${helpersText(counts.total, counts.working)})`}
      title={helpersText(counts.total, counts.working)}
      onClick={onToggle}
      className={`${className ?? ""} inline-flex h-6 items-center gap-0.5 rounded-md px-1 text-faint hover:bg-muted hover:text-foreground`}
    >
      <HelperCount total={counts.total} working={counts.working} />
      <ChevronDownIcon
        aria-hidden="true"
        className={`size-3 transition-transform duration-(--dur-fast) ${open ? "rotate-180" : ""}`}
      />
    </button>
  );
}

// The viewer's open leads in the Chat sidebar, per company and person, in this browser ----------

const OPEN_KEY = (co: string, me: string | null, where = "chat") =>
  `cos.${where}.helpers.${co}.${me ?? "operator"}`;

export function readOpenLeads(co: string, me: string | null, where = "chat"): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(OPEN_KEY(co, me, where));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    return new Set();
  }
}

function writeOpenLeads(co: string, me: string | null, where: string, open: ReadonlySet<string>) {
  try {
    if (open.size === 0) globalThis.localStorage?.removeItem(OPEN_KEY(co, me, where));
    else globalThis.localStorage?.setItem(OPEN_KEY(co, me, where), JSON.stringify([...open]));
  } catch {
    // Storage off (private window): open for this visit only.
  }
}

/**
 * Which leads' helper lists are open (closed by default), and a toggle; remembered per viewer and
 * per place (`chat`: the Team list; `workforce`: the Workforce sidebar).
 */
export function useOpenLeads(
  co: string,
  me: string | null,
  where = "chat",
): [ReadonlySet<string>, (lead: string) => void] {
  const key = `${where}:${co}:${me}`;
  const [state, setState] = useState(() => ({ key, open: readOpenLeads(co, me, where) }));
  const open = state.key === key ? state.open : readOpenLeads(co, me, where);
  const toggle = (lead: string) => {
    const next = new Set(open);
    if (next.has(lead)) next.delete(lead);
    else next.add(lead);
    writeOpenLeads(co, me, where, next);
    setState({ key, open: next });
  };
  return [open, toggle];
}

/**
 * DE page › Helpers: one card per helper — the AG badge, its name, what it does (its role's first
 * line), every skill, working or idle, its last activity; Ask the DE; and for whoever may change
 * it, the roster's hover icons (Pause, Take over, Reassign, Edit, Retire). Then "Ask for a
 * helper" and this DE's helper requests with their stages. `focus` scrolls to and marks one
 * helper.
 */
export function DeHelpersTab({
  api,
  company,
  de,
  helpers,
  all = [],
  available = [],
  meId,
  canMessage,
  now = Date.now(),
  focus,
}: {
  api: Api;
  company: CompanyAccess;
  de: { membership_id: string; display_name: string; role_skill_keys: readonly string[] };
  helpers: readonly Helper[];
  /** HELPERS-2: every helper of the company, to offer "Use a helper" from. */
  all?: readonly Helper[];
  /** Not-deployed helpers, to start a request from. */
  available?: readonly Helper[];
  meId: string | null;
  /** The viewer may open a DM (Chat on and writable, a member). */
  canMessage: boolean;
  /** The minute clock (`useHelpers`), so "working" lapses on its own. */
  now?: number;
  focus?: string | undefined;
}) {
  const choices = useAgChoices(
    api,
    company.id,
    helpers.some((a) => a.can_edit),
  );
  const ask = useAskTheDe(api, company, meId);
  const { total, working } = helperCounts(helpers, now);
  useEffect(() => {
    if (!focus) return;
    document
      .querySelector(`[data-helper="${CSS.escape(focus)}"]`)
      ?.scrollIntoView?.({ block: "center" });
  }, [focus]);
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="de-helpers">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <p className="m-0 flex items-center gap-1 text-sm text-muted-foreground">
          <span data-slot="de-helpers-count">
            {helpersText(total, working) || "No helpers yet"}
          </span>
          <HelpersInfoTip />
        </p>
        <span className="ml-auto flex items-center gap-1">
          <UseHelperButton api={api} company={company} de={de} all={all} />
          <AskForHelperButton
            api={api}
            company={company}
            meId={meId}
            de={de}
            available={available}
          />
        </span>
      </div>
      {de.role_skill_keys.length ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-slot="de-own-skills">
          <span className="text-xs text-muted-foreground">Own skills</span>
          <SkillChips skills={de.role_skill_keys} company={company} />
        </div>
      ) : null}
      {helpers.length ? (
        <div className="rounded-lg border border-border" data-slot="de-helper-cards">
          {helpers.map((a) => {
            const on = helperWorking(a, now);
            const activity = activityLabel(a);
            const name = workerName(a.name);
            return (
              <div
                key={a.id}
                data-row
                data-helper={a.id}
                data-focused={focus === a.id ? "true" : undefined}
                className={cn(
                  "group/helper relative flex min-w-0 flex-col gap-1 border-b border-subtle px-3 py-2.5 last:border-b-0",
                  focus === a.id ? "bg-primary-soft/60" : "hover:bg-muted/50",
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <WorkerBadge kind="ag" size="sm" />
                  <Link
                    to="/c/$slug/agents/ag/$id"
                    params={{ slug: company.slug, id: a.id }}
                    search={keepFocus}
                    className="min-w-0 truncate text-sm font-medium text-foreground hover:underline"
                  >
                    {name}
                  </Link>
                  <HelperDot working={on} />
                  <RecallKeysChip agent={a} />
                  {isBorrowed(a, de.membership_id) ? (
                    <span
                      className="shrink-0 truncate rounded-full bg-muted px-1.5 text-2xs leading-4 text-muted-foreground"
                      data-slot="helper-shared"
                      title={`Reports to ${workerName(a.reports_to.name)}`}
                    >
                      Shared · {workerName(a.reports_to.name)}
                    </span>
                  ) : null}
                  <span
                    className="ml-auto inline-flex min-w-0 shrink items-center gap-1.5 text-xs text-muted-foreground group-hover/helper:invisible group-focus-within/helper:invisible max-sm:hidden"
                    data-slot="helper-last"
                    title={activity?.full ?? undefined}
                  >
                    <span className="truncate">
                      {on
                        ? "Working"
                        : a.status === "paused"
                          ? "Paused"
                          : (activity?.text ?? "No activity reported")}
                    </span>
                    {activity?.when ? (
                      <time
                        dateTime={a.last_activity_at ?? undefined}
                        className="shrink-0 tabular-nums"
                      >
                        {activity.when}
                      </time>
                    ) : null}
                  </span>
                </div>
                {a.role ? (
                  <p
                    className="m-0 truncate pl-8 text-xs text-muted-foreground"
                    data-slot="helper-role"
                  >
                    {firstLine(a.role)}
                  </p>
                ) : null}
                {a.skills.length ? (
                  <div className="pl-8">
                    <SkillChips skills={a.skills} company={company} />
                  </div>
                ) : null}
                <div
                  data-slot="helper-actions"
                  className="absolute top-2 right-2 flex items-center gap-px rounded-md bg-background/95 opacity-0 group-hover/helper:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100"
                >
                  {canMessage ? (
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Ask ${workerName(de.display_name)} about ${name}`}
                      title="Ask the DE"
                      className="text-faint hover:text-foreground"
                      disabled={ask.isPending}
                      onClick={(e) => {
                        e.preventDefault();
                        ask.mutate({ deId: de.membership_id, helperName: a.name });
                      }}
                    >
                      <MessageSquareIcon />
                    </Button>
                  ) : null}
                  {isBorrowed(a, de.membership_id) ? (
                    <StopUsingButton api={api} company={company} helper={a} de={de} />
                  ) : null}
                  <AgActions
                    api={api}
                    company={company}
                    agent={a}
                    choices={choices}
                    variant="row"
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <HelperRequestsList api={api} company={company} deId={de.membership_id} meId={meId} />
    </div>
  );
}

/** The first line of a role text (what it does, in one line). */
export function firstLine(text: string): string {
  return (
    text
      .split("\n")
      .find((l) => l.trim())
      ?.trim() ?? ""
  );
}
